import { useMutation } from '@tanstack/react-query';
import { apiFetch } from './api.js';

export type FeedbackTargetType = 'project' | 'account' | 'contact' | 'signal';

// Every loaded sub-resource the snapshot may capture. Keyed the same as
// buildReportContext's input so the singular/plural sections stay in sync.
export interface ReportContextRecords {
  project?: unknown;
  account?: unknown;
  contact?: unknown;
  signal?: unknown;
  accounts?: unknown[];
  contacts?: unknown[];
  signals?: unknown[];
  projects?: unknown[];
}

export interface ReportContext {
  route?: string;
  records: ReportContextRecords;
}

// Builds a compact JSON snapshot of what was on screen. Pure and defensive: any
// section may be absent. The drawer tabs are uncontrolled, so we capture all
// loaded sub-resources rather than a single active tab.
export function buildReportContext(
  input: {
    route?: string;
  } & ReportContextRecords,
): ReportContext {
  const records: ReportContextRecords = {};
  for (const key of ['project', 'account', 'contact', 'signal'] as const) {
    if (input[key] !== undefined) records[key] = input[key];
  }
  for (const key of ['accounts', 'contacts', 'signals', 'projects'] as const) {
    if (input[key] !== undefined) records[key] = input[key];
  }
  return { route: input.route, records };
}

export interface ReportDataIssueInput {
  targetType: FeedbackTargetType;
  targetId: string;
  body: string;
  context?: ReportContext;
}

export function useReportDataIssue(workspaceId: string | undefined) {
  return useMutation({
    mutationFn: async (input: ReportDataIssueInput) => {
      if (!workspaceId) {
        throw new Error('workspaceId is required to report a data issue');
      }
      const qs = new URLSearchParams({ workspace_id: workspaceId });
      return apiFetch<{ data: { id: string }; metadata: { credits: number } }>(`/feedback?${qs}`, {
        method: 'POST',
        body: JSON.stringify({
          target_type: input.targetType,
          target_id: input.targetId,
          body: input.body,
          source: 'web',
          context: input.context ?? { records: {} },
        }),
      });
    },
  });
}
